import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { resetPassword, generateResetCode, confirmResetCode } from "../store/auth/thunks";
import { useNavigate } from "react-router-dom";
import '../styles/login.css';
import '../styles/profile.css';

import {
  Flex,
  Heading,
  Input,
  Button,
  InputGroup,
  Stack,
  InputLeftElement,
  Text,
  chakra,
  Box,
  Spinner,
  useColorMode,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  Link,
  FormControl,
  FormHelperText,
  InputRightElement,
  Center,
} from "@chakra-ui/react";
import { FaUserAlt, FaLock } from "react-icons/fa";
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import useToastNotification from "../hooks/useToastNotification";
import useAsyncState from "../hooks/useAsyncState";


const CFaUserAlt = chakra(FaUserAlt);
const CFaLock = chakra(FaLock);

export default function Login() {
  const cancelRef = React.useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showToast = useToastNotification();
  const { info } = useAsyncState({
    onError: (info) => showToast(info?.response?.data?.result, { status: "error" }),
  });
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [new_pass_1, setNewPass1] = useState("");
  const [new_pass_2, setNewPass2] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [showNewPassword1, setShowNewPassword1] = useState(false);
  const [showNewPassword2, setShowNewPassword2] = useState(false);

  const [enterCode, setEnterCode] = useState(false);
  const [enterPw, setEnterPw] = useState(false);

  const authLoading = useSelector((state) => state.ui.loading);

  const { colorMode } = useColorMode();

  const handleSubmit = async(e) => {
    e.preventDefault();
    await dispatch(generateResetCode({ email }));
  };

  const handleSubmitCode = async(e) => {
    e.preventDefault();
    await dispatch(confirmResetCode({ email, code }));
  };

  const handleChangePassword = (e) => {
    setEnterPw(false);
  };

  const handleConfirmChangePw = async (e) => {
    e.preventDefault();
    await dispatch(
      resetPassword({
        code,
        email,
        new_pass_1,
        new_pass_2
      })
    );
    setEnterPw(false);
    setEnterCode(false);
  };

  useEffect(() => {
    evaluateInfo();
  }, [info]);

  const evaluateInfo = () => {
    if(info?.result === "Reset Code Generated Successfully"){
        setEnterCode(true);
    }
    if(info?.result === "Reset Code Verified Successfully"){
        setEnterPw(true);
    }
    if(info?.result === "Password Reset Successfully"){
        navigate("/login");
    }
  }



  return (
    <Flex
      direction="column"
      minH="100vh"
      bg={colorMode === 'light' ? "gray.200" : "gray.800"}
      justifyContent="center"
      alignItems="center"
    >
      <Stack
        flexDir="column"
        mb="2"
        justifyContent="center"
        alignItems="center"
      >
        <Box minW={{ base: "90%", md: "468px" }} rounded="lg" overflow="hidden" boxShadow="2px 4px 4px rgba(0,0,0,0.2)">
        {authLoading ?
          <Stack
              spacing={4}
              p="1rem"
              backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
              boxShadow="md"
            >
            <Center>
            <Spinner
                thickness='4px'
                speed='0.65s'
                emptyColor='gray.200'
                color='blue.500'
                size='xl'
            />
            </Center>
          </Stack>
        :
          <form>
            <Stack
              spacing={3}
              p="1rem"
              backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
              boxShadow="md"
            >
              <Link href="/" color="blue.500" fontSize="sm" ml={1} mb={-1}>
                ← Back to home
              </Link>
              <Heading className="page-heading" fontSize="xl" ml={1} mb={-1} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Reset Password</Heading>
              <FormControl>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children={<CFaUserAlt color="gray.300" />}
                  />
                  <Input
                    type="email"
                    placeholder="email address"
                    value={email}
                    isDisabled={enterCode}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </InputGroup>
              </FormControl>
              {!enterCode ?
              <Button
                borderRadius="md"
                type="submit"
                variant="solid"
                colorScheme="blue"
                width="full"
                onClick={handleSubmit}
              >
                Reset
              </Button>
              :
              <>
              <Center>
                <Text textAlign="center" fontSize="sm">
                    Reset code sent to <b>{email}</b>. Code expires in 15 minutes.
                </Text>
              </Center>
              <FormControl>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    color="gray.300"
                    children={<CFaLock color="gray.300" />}
                  />
                  <Input
                    type={showCode ? "text" : "password"}
                    placeholder="Enter Reset Code"
                    onChange={(e) => setCode(e.target.value)}
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      variant="ghost"
                      onClick={() => setShowCode((prev) => !prev)}
                    >
                      {showCode ? <ViewIcon /> : <ViewOffIcon />}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <Center>
                  <Text fontSize="sm">
                    Didn't get a code?&nbsp;
                  </Text>
                  <Link color="blue.500" fontSize="sm" onClick={handleSubmit}>
                    Send Another
                  </Link>
              </Center>
              <Button
                borderRadius="md"
                type="submit"
                variant="solid"
                colorScheme="blue"
                width="full"
                onClick={handleSubmitCode}
              >
                Submit Code
              </Button>
            <AlertDialog
              motionPreset='slideInBottom'
              isOpen={enterPw}
              leastDestructiveRef={cancelRef}
              onClose={e => handleChangePassword(e)}
              isCentered={true}
              closeOnOverlayClick={false}
            >
            {authLoading ?
            <AlertDialogOverlay>
              <AlertDialogContent>
              <Center>
                <Spinner
                    thickness='4px'
                    speed='0.65s'
                    emptyColor='gray.200'
                    color='blue.500'
                    size='xl'
                />
              </Center>
              </AlertDialogContent>
            </AlertDialogOverlay>
            :
              <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  Change Password
                </AlertDialogHeader>
                <AlertDialogCloseButton onClick={e => handleChangePassword(e)}/>
                <AlertDialogBody>
                  <FormControl>
                    <FormHelperText mb={2} ml={1}>
                      New Password *
                    </FormHelperText>
                    <InputGroup>
                      <Input
                        type={showNewPassword1 ? "text" : "password"}
                        onChange={(e) => setNewPass1(e.target.value)}
                      />
                      <InputRightElement width="4.5rem">
                        <Button
                          variant="ghost"
                          onClick={() =>
                            setShowNewPassword1((showNewPassword1) => !showNewPassword1)
                          }
                        >
                          {showNewPassword1 ? <ViewIcon /> : <ViewOffIcon />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                  <FormControl>
                    <FormHelperText mb={2} ml={1}>
                      Confirm New Password *
                    </FormHelperText>
                    <InputGroup>
                      <Input
                        type={showNewPassword2 ? "text" : "password"}
                        onChange={(e) => setNewPass2(e.target.value)}
                      />
                      <InputRightElement width="4.5rem">
                        <Button
                          variant="ghost"
                          onClick={() =>
                            setShowNewPassword2((showNewPassword2) => !showNewPassword2)
                          }
                        >
                          {showNewPassword2 ? <ViewIcon /> : <ViewOffIcon />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button colorScheme='blue' borderRadius="md" onClick={e => handleConfirmChangePw(e)} ml={3}>
                    Change Password
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
              </AlertDialogOverlay>
            }
            </AlertDialog>
              </>
              }
              <Text textAlign="center" fontSize="sm">
                <Link color="blue.500" href="/login">
                  Return to Log in
                </Link>
              </Text>
            </Stack>
          </form>
        }
        </Box>
      </Stack>
    </Flex>
  );
}